import React, { useState, useMemo, ReactNode } from 'react'
import {
  useFloating,
  offset,
  flip,
  shift,
  arrow,
  Placement,
  Side,
  useDismiss,
  useInteractions,
  autoUpdate,
} from '@floating-ui/react'
import { Popover } from '@headlessui/react'
import classNames from 'classnames'
import { IconColor } from '@/atoms/utils/IconTypes'
import type { NamedColor } from '@/constants/types'

export interface BaseTooltipProps {
  iconSize?: number
  panelPosition?: 'LEFT' | 'CENTER' | 'RIGHT'
  guildScore?: number | undefined
  titleText?: string
  children?: ReactNode
  buttonColor?: IconColor
  textColor?: NamedColor
  slug?: string
  className?: string
}

interface BaseTooltipComponentProps extends BaseTooltipProps {
  trigger: ReactNode
  content: ReactNode
}

export const ParentsGuideTooltip: React.FC<BaseTooltipComponentProps> = ({
  panelPosition = 'CENTER',
  trigger,
  content,
  className,
}) => {
  const [isHovering, setIsHovering] = useState(false)

  const arrowRef = React.useRef<HTMLDivElement>(null)

  const placement: Placement =
    panelPosition === 'LEFT' ? 'bottom-start' : panelPosition === 'RIGHT' ? 'bottom-end' : 'bottom'

  const { x, y, refs, strategy, middlewareData, context } = useFloating({
    placement,
    middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
    open: isHovering,
    onOpenChange: (open) => {
      setIsHovering(open)
    },
  })

  // Handle clicks outside the tooltip
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const staticSide = useMemo(() => {
    const side = placement.split('-')[0] || ('' as Side)
    return {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[side] as string
  }, [placement])

  return (
    <Popover className={classNames('mt-2', className)}>
      {({ open }) => (
        <>
          <Popover.Button
            ref={refs.setReference}
            {...getReferenceProps({
              onMouseEnter: () => setIsHovering(true),
              onMouseLeave: () => setIsHovering(false),
            })}
          >
            {trigger}
          </Popover.Button>
          {(open || isHovering) && (
            <div
              ref={refs.setFloating}
              {...getFloatingProps()}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: 'calc(100vw - 2rem)',
                maxWidth: '320px',
              }}
              className={classNames('p-6 !z-[60] text-left bg-white shadow-light-2 rounded-2xl')}
            >
              <div
                ref={arrowRef}
                className="absolute h-3 w-3 rotate-45 bg-white"
                style={{
                  left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : '',
                  top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : '',
                  right: '',
                  bottom: '',
                  [staticSide]: '-5px',
                }}
              />
              {content}
            </div>
          )}
        </>
      )}
    </Popover>
  )
}
